<template>
  <div class="championship-bracket-wrap">
    <div class="bracket-scroll">
      <div ref="grid" class="bracket-grid">
        <svg
          v-if="connectorPaths.length"
          class="bracket-connectors"
          :width="connectorBox.w"
          :height="connectorBox.h"
          aria-hidden="true"
        >
          <path
            v-for="conn in connectorPaths"
            :key="conn.id"
            :d="conn.d"
            :stroke="conn.advanced ? '#009f63' : '#c0c0c0'"
            :stroke-width="conn.advanced ? 2 : 1.5"
            fill="none"
          />
        </svg>

        <div
          v-for="(group, idx) in groupedStages"
          :key="group.stage ? group.stage.id : idx"
          class="bracket-column"
          :class="{ 'final-shield-column': isFinalShieldColumn(idx), 'is-first-round': idx === 0 }"
        >
          <div class="bracket-column-header-row">
            <div class="bracket-column-header">{{ stageLabel(group.stage) }}</div>
            <!-- "Advance" CTA intentionally hidden — kept the handler logic below
                 (onAdvance/canAdvance/advanceTooltip) so it can be re-enabled if needed. -->
          </div>
          <div class="bracket-column-body">
            <!-- Once a skinned champion is decided, the final column shows the
                 champion shield as its bracket card (replacing the final match
                 card). Dedicated ref so computeConnectors can target it
                 directly (not via the match-id lookup). -->
            <div
              v-if="isFinalShieldColumn(idx)"
              ref="championShieldSlot"
              class="bracket-match-slot final-shield-slot"
              role="button"
              tabindex="0"
              @click="finalMatch && $emit('match-click', finalMatch)"
              @keyup.enter="finalMatch && $emit('match-click', finalMatch)"
            >
              <SkinnedTournamentOverviewCard
                championship-variant
                :participant-data="championProxyData"
                :theme-code-override="championThemeCode"
                :score-box-value="championGoalsValue"
                :score-box-label="goalsEquivalenceLabel"
              />
            </div>
            <template v-else>
              <div
                v-for="match in group.matches"
                :key="match.id"
                :ref="`match-${match.id}`"
                class="bracket-match-slot"
              >
                <ChampionshipMatchCard
                  :match="match"
                  :stage="group.stage"
                  :participants-by-user-id="participantsByUserId"
                  @match-click="(m) => $emit('match-click', m)"
                />
              </div>
            </template>
          </div>
        </div>
        <!-- Non-skinned champion: keep the standalone gold "Champion" column.
             Skinned champions render the shield inside the final column above. -->
        <div
          v-if="champion && !championIsSkinned"
          class="bracket-column champion-column"
        >
          <div class="bracket-column-header champion-header">{{ $t('championship.champion') }}</div>
          <div class="bracket-column-body">
            <div class="champion-card">
              <div class="champion-rank">1</div>
              <div class="champion-trophy">
                <img
                  v-if="championProfilePicture"
                  :src="championProfilePicture"
                  :alt="champion.login"
                  class="champion-avatar"
                />
                <i v-else class="fa-regular fa-crown" aria-hidden="true"></i>
              </div>
              <div class="champion-label">{{ $t('championship.winner') }}</div>
              <div class="champion-name special-text">{{ champion.login }}</div>
              <div class="champion-volume-value">
                {{ $t('championship.matchDetails.goalsAmount', { count: formatGoals(championScore) }) }}
              </div>
              <div class="champion-volume-label">{{ goalsEquivalenceLabel }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChampionshipMatchCard from './ChampionshipMatchCard.vue';
import SkinnedTournamentOverviewCard from '@/components/SkinnedTournamentOverviewCard.vue';
import ChampionshipRepository from '@/repositories/championship';
import { buildSkinFromServer } from '@/services/skins.service';
import {
  groupMatchesByStageWithPlaceholders,
  stageLabel,
  canAdvanceStage,
  getLotsPerGoal,
} from '@/services/championship.service';

export default {
  name: 'ChampionshipBracket',
  components: { ChampionshipMatchCard, SkinnedTournamentOverviewCard },
  props: {
    overview: { type: Object, default: null },
    bracket: { type: Object, default: null },
    participants: { type: Array, default: () => [] },
  },
  data() {
    return {
      advancingStageId: null,
      // SVG connector geometry — recomputed after layout settles and on resize.
      connectorPaths: [],
      connectorBox: { w: 0, h: 0 },
      _connectorRaf: null,
      _resizeObserver: null,
    };
  },
  mounted() {
    this.scheduleConnectorRecompute();
    if (typeof ResizeObserver !== 'undefined' && this.$refs.grid) {
      this._resizeObserver = new ResizeObserver(() => this.scheduleConnectorRecompute());
      this._resizeObserver.observe(this.$refs.grid);
    }
    window.addEventListener('resize', this.scheduleConnectorRecompute);
  },
  beforeDestroy() {
    if (this._connectorRaf) cancelAnimationFrame(this._connectorRaf);
    if (this._resizeObserver) this._resizeObserver.disconnect();
    window.removeEventListener('resize', this.scheduleConnectorRecompute);
  },
  watch: {
    bracket: { handler() { this.scheduleConnectorRecompute(); }, deep: true },
    overview: { handler() { this.scheduleConnectorRecompute(); }, deep: true },
    // Participants arrive after bracket/overview; once they do the champion
    // shield replaces the final match card and shifts layout, so connectors
    // must be recomputed (otherwise the line into the shield never draws).
    participants: { handler() { this.scheduleConnectorRecompute(); }, deep: true },
  },
  computed: {
    groupedStages() {
      // Pad missing stages / matches with placeholders so the full bracket
      // silhouette is always visible — the design shows the skeleton even
      // when later rounds haven't seeded matches yet.
      // Temporary: hide the QUALIFICATION stage — current clients start the
      // bracket at Round of 16. Remove this filter to show qualification again.
      return groupMatchesByStageWithPlaceholders(this.bracket, this.overview)
        .filter((g) => g.stage?.stageType !== 'QUALIFICATION');
    },
    participantsByUserId() {
      const map = {};
      (this.participants || []).forEach((p) => { if (p?.userId != null) map[p.userId] = p; });
      return map;
    },
    champion() {
      return (this.participants || []).find((p) => p.status === 'CHAMPION') || null;
    },
    // Skinned championships render the winner with the skin proxy card; plain
    // ones keep the gold champion card.
    championIsSkinned() {
      return !!(this.champion && (this.champion.skinCharacterCode || this.champion.skinImageUrl));
    },
    // The final match = the real match in the last bracket column (the one the
    // shield represents). Derived from the rendered groups rather than a
    // nextMatchId lookup, which is unreliable when the backend doesn't populate
    // nextMatchId and would otherwise resolve to the first completed match.
    finalMatch() {
      const groups = this.groupedStages || [];
      const last = groups[groups.length - 1];
      const m = last && last.matches && last.matches[0];
      return m && !m.placeholder ? m : null;
    },
    // Champion avatar comes straight off the (public) championship participant's
    // skin image — no profile picture is exposed for non-skinned winners.
    championProfilePicture() {
      return this.champion?.skinImageUrl || null;
    },
    // Champion score = the winning side's score on the final match.
    championScore() {
      const m = this.finalMatch;
      if (!m || m.winnerParticipantId == null) return null;
      if (m.winnerParticipantId === m.participantAId) return m.scoreA ?? null;
      if (m.winnerParticipantId === m.participantBId) return m.scoreB ?? null;
      return null;
    },
    // The skin theme code isn't on the public championship payload, so the proxy
    // card renders without the themed background/logo (character image, name and
    // score still show).
    championThemeCode() {
      return null;
    },
    // Shape the champion into the participantData the proxy card expects. Reuse
    // buildSkinFromServer so the skin object (name split, country fallback,
    // image/category) matches how the tournament views build it.
    championProxyData() {
      return {
        participantId: null,
        score: this.championScore,
        place: 1,
        status: 'WINNER',
        skin: buildSkinFromServer(this.champion, this.championThemeCode) || {},
      };
    },
    championGoalsValue() {
      return this.$t('championship.matchDetails.goalsAmount', { count: this.formatGoals(this.championScore) });
    },
    // "Each goal equals N lots" — N is hardcoded per championship for now
    // (see getLotsPerGoal); backend will provide it as a param later.
    goalsEquivalenceLabel() {
      return this.$t('championship.matchDetails.goalsEquivalence', { lots: getLotsPerGoal(this.overview) });
    },
    finalColumnIndex() {
      return (this.groupedStages || []).length - 1;
    },
    // finalMatch (above) is the decided final; its id is the shield slot's ref
    // (so connectors still draw into the shield) and the object is emitted on
    // click so the shield opens the Head-to-Head modal like a match card.
    finalMatchId() {
      return this.finalMatch ? this.finalMatch.id : null;
    },
  },
  methods: {
    // True for the final column once a skinned champion is decided — that
    // column renders the champion shield instead of the final match card.
    isFinalShieldColumn(idx) {
      return this.championIsSkinned
        && idx === this.finalColumnIndex
        && this.finalMatchId != null;
    },
    // Score rendered as a whole number; the "Goals" unit and equivalence copy
    // live in i18n (matchDetails.goalsAmount / goalsEquivalence).
    formatGoals(v) {
      if (v == null) return '0';
      return new Intl.NumberFormat(this.$i18n.locale, { maximumFractionDigits: 0 }).format(v);
    },
    stageLabel(stage) {
      if (!stage) return '';
      const i18nKey = stage.stageType ? `championship.stage.${stage.stageType}` : '';
      const translated = i18nKey ? this.$t(i18nKey) : '';
      // Use translation if the key exists; otherwise fall back to the service helper.
      return translated && translated !== i18nKey ? translated : stageLabel(stage);
    },
    canAdvance(stage) {
      return canAdvanceStage(stage);
    },
    // Compute L-shaped SVG paths between adjacent column matches. Walks the
    // rendered grid by column index so connectors render even for placeholder
    // matches (later rounds where the backend hasn't seeded yet) — relying on
    // each real match's `nextMatchId` would skip those entirely. Stroke turns
    // green when both source and target are real, source completed, and the
    // winner has been forwarded into the target slot.
    computeConnectors() {
      const grid = this.$refs.grid;
      const groups = this.groupedStages || [];
      if (!grid || groups.length < 2) {
        this.connectorPaths = [];
        return;
      }
      const gridRect = grid.getBoundingClientRect();
      const paths = [];
      for (let c = 0; c < groups.length - 1; c++) {
        const sourceMatches = groups[c].matches || [];
        const targetMatches = groups[c + 1].matches || [];
        if (!sourceMatches.length || !targetMatches.length) continue;
        for (let r = 0; r < sourceMatches.length; r++) {
          const sourceMatch = sourceMatches[r];
          const targetMatch = targetMatches[Math.floor(r / 2)];
          if (!sourceMatch || !targetMatch) continue;
          const sourceRefs = this.$refs[`match-${sourceMatch.id}`];
          // The final column may be the champion shield (which replaces the
          // match card) — target it via its dedicated ref so the line draws
          // regardless of match-id matching.
          const targetRefs = this.isFinalShieldColumn(c + 1)
            ? this.$refs.championShieldSlot
            : this.$refs[`match-${targetMatch.id}`];
          const sourceEl = Array.isArray(sourceRefs) ? sourceRefs[0] : sourceRefs;
          const targetEl = Array.isArray(targetRefs) ? targetRefs[0] : targetRefs;
          if (!sourceEl || !targetEl) continue;
          const s = sourceEl.getBoundingClientRect();
          const t = targetEl.getBoundingClientRect();
          const sx = s.right - gridRect.left;
          const sy = s.top + s.height / 2 - gridRect.top;
          const tx = t.left - gridRect.left;
          const ty = t.top + t.height / 2 - gridRect.top;
          const midX = sx + (tx - sx) / 2;
          const d = `M ${sx} ${sy} H ${midX} V ${ty} H ${tx}`;
          // Green for every advancement: the source match has a winner who has
          // moved into the target match. Pending/undecided lines stay default.
          const advanced = !sourceMatch.placeholder
            && sourceMatch.winnerUserId != null
            && (targetMatch.userAId === sourceMatch.winnerUserId
              || targetMatch.userBId === sourceMatch.winnerUserId);
          paths.push({ id: `c-${sourceMatch.id}-${targetMatch.id}`, d, advanced });
        }
      }
      this.connectorPaths = paths;
      this.connectorBox = { w: grid.scrollWidth, h: grid.scrollHeight };
    },
    scheduleConnectorRecompute() {
      this.$nextTick(() => {
        // Two requestAnimationFrames so the DOM has fully laid out (especially
        // after font/icon load shifts the match-card heights).
        if (this._connectorRaf) cancelAnimationFrame(this._connectorRaf);
        this._connectorRaf = requestAnimationFrame(() => {
          requestAnimationFrame(() => this.computeConnectors());
        });
      });
    },
    advanceTooltip(stage) {
      if (!stage) return '';
      if (stage.advancementCompletedAt) return this.$t('championship.bracket.alreadyAdvanced');
      if (stage.tournamentState !== 'FINISHED') return this.$t('championship.bracket.tournamentNotFinished');
      return this.$t('championship.bracket.advanceTooltip');
    },
    async onAdvance(stage) {
      if (!stage || !this.canAdvance(stage) || this.advancingStageId === stage.id) return;
      this.advancingStageId = stage.id;
      try {
        await ChampionshipRepository.advanceFromStage(stage.id);
        this.$emit('stage-advanced', stage);
      } catch (err) {
        console.error('[championship] advance stage failed', err);
        this.$emit('stage-advance-failed', { stage, error: err });
      } finally {
        this.advancingStageId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.championship-bracket-wrap {
  width: 100%;
}

.bracket-scroll {
  width: 100%;
  overflow-x: auto;
  padding-bottom: 12px;
}

.bracket-grid {
  display: inline-flex;
  // Stretch all columns to the tallest (the first round) so later rounds can
  // distribute their fewer cards across the same height and line up with the
  // midpoints of their feeder matches.
  align-items: stretch;
  gap: 56px;
  padding: 12px 4px 24px;
  min-width: 100%;
  position: relative;
}

// SVG connector layer sits inside the same scroll container as the cards, so
// it scales with horizontal scroll. pointer-events: none so clicks still hit
// the match cards underneath.
.bracket-connectors {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 0;
  overflow: visible;
}

.bracket-column {
  display: flex;
  flex-direction: column;
  min-width: 200px;
  width: 220px;
  position: relative;
  z-index: 1; // ensure cards float above the SVG connector layer
}

.bracket-column-header-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.bracket-column-header {
  align-self: flex-start;
  background: var(--championship-accent, #c03131);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 16px;
  border-radius: 16px;
  letter-spacing: 0.3px;
}

.advance-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #1b1b1b;
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    background: #333;
  }

  &.is-disabled,
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  i {
    font-size: 10px;
  }

  .advance-btn-label {
    line-height: 1;
  }
}

.bracket-column-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  // Later rounds distribute their cards evenly across the full column height;
  // space-around centers each card in an equal slot, so a round-N card lands
  // exactly at the midpoint of its two round-(N-1) feeders.
  justify-content: space-around;
}

// First round defines the bracket height with fixed gaps between its matches.
.bracket-column.is-first-round .bracket-column-body {
  justify-content: flex-start;
  gap: 16px;
}

.bracket-match-slot {
  flex: none;
}

.champion-column {
  align-self: center;
  margin-top: 0;
}

// Final column when it holds the champion shield: wider than a normal column
// and full height so the shield sits centered, fed by the bracket connectors.
.final-shield-column {
  width: 300px;
  min-width: 300px;
  // Full height so the "Final" header stays pinned at the top (aligned with the
  // other stage headers) while the shield is centered vertically in the body.
  align-self: stretch;

  .bracket-column-body {
    flex: 1;
    align-items: center;
    justify-content: center;
  }
}

.final-shield-slot {
  margin: 0;
  width: 100%;
  cursor: pointer;
  transition: transform 0.18s ease;

  &:hover {
    transform: translateY(-2px);
  }
}

.champion-header {
  background: #ffb801;
  // The champion column has no header-row wrapper (unlike the other columns),
  // so add the same gap the bracket headers use to keep the badge off the card.
  margin-bottom: 12px;
}

.champion-card {
  background: linear-gradient(180deg, #fff7d6 0%, #ffe199 100%);
  border: 1px solid #ffcd47;
  border-radius: 16px;
  padding: 18px 16px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.champion-rank {
  font-family: 'Aeonik', 'Avenir', sans-serif;
  font-weight: 800;
  font-size: 28px;
  color: #c25c08;
}

.champion-trophy i {
  font-size: 28px;
  color: #c25c08;
}

.champion-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #ffcd47;
  background: #fff;
}

.champion-label {
  font-size: 12px;
  color: #c25c08;
  font-weight: 600;
}

.champion-name {
  font-size: 18px;
  font-weight: 700;
  color: #1b1b1b !important;
}

.champion-volume-value {
  font-size: 18px;
  font-weight: 700;
  color: #1b1b1b;
  margin-top: 8px;
}

.champion-volume-label {
  font-size: 11px;
  color: #6a4500;
}

.theme--dark .championship-bracket-wrap {
  .bracket-column-header { background: #c03131; }
  .champion-header { background: #ffb801; color: #1b1b1b; }
}
</style>
